/**
 * Billing Hook
 *
 * Provides billing and subscription information from the workspace subscription data.
 * Supports both preview mode (dummy data) and production mode (real backend data).
 *
 * Trial days calculation: Matches reference implementation by calculating client-side
 * from freeTrialEnd date in UTC to avoid timezone inconsistencies with backend.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/i18n/format';
import { useCurrentUser } from './use-current-user';
import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from './use-workspace-id';
import { useGetCreditUsageSummaryQuery, type AgentCreditUsage, type CreditSource, CreditSourceKind } from '@/lib/graphql/types-and-hooks';

/**
 * Calculate days remaining in trial from freeTrialEnd date.
 * Uses UTC calendar days to match reference implementation and avoid timezone issues.
 */
function calculateTrialDaysRemaining(freeTrialEnd: string | null | undefined): number | null {
  if (!freeTrialEnd) return null;

  const now = new Date();
  const trialEnd = new Date(freeTrialEnd);

  // Convert to UTC and get start of day (midnight) for calendar day comparison
  const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const trialEndUtc = Date.UTC(trialEnd.getUTCFullYear(), trialEnd.getUTCMonth(), trialEnd.getUTCDate());

  // Difference in milliseconds
  const diffMs = trialEndUtc - nowUtc;

  // Convert to days and truncate
  const diffDays = Math.trunc(diffMs / (1000 * 60 * 60 * 24));

  // Never go negative
  return Math.max(diffDays, 0);
}

export interface BillingInfo {
  // Plan info
  planName: string;
  planPrice: number;
  billingCycle: string;
  billingPeriod: string;
  currency: string;

  // Trial info
  trialDaysRemaining: number | null;
  isInTrial: boolean;

  // Subscription status
  subscriptionStatus: string;

  // Credits
  totalCreditsUsed: number | null;
  totalCreditsLimit: number | null;
  // Recurring per-term subscription allotment (excludes one-time packs). Used to
  // derive the real credits/seat figure shown in the out-of-credits dialog.
  subscriptionCreditsLimit: number | null;

  // Per-source breakdown (subscription quota + each non-expired pack)
  creditSources: CreditSource[] | null;

  // Per-agent breakdown
  perAgent: AgentCreditUsage[] | null;

  // Seats
  seats: number;
}

export function useBilling() {
  const { currentUser, loading: userLoading } = useCurrentUser();
  const preview = isLovablePreview();
  // In the memo deps below so the billing period re-renders on a language switch.
  const { i18n } = useTranslation();
  const language = i18n.language;

  const activeWorkspaceId = useWorkspaceId();

  // Find the active workspace's subscription data
  const activeWorkspace = currentUser?.workspaces?.find(w => w.id === activeWorkspaceId);
  const subscription = activeWorkspace?.subscription;

  // Fetch credit usage summary (skip in preview mode or when no workspace)
  const workspaceIdNum = activeWorkspaceId ? parseInt(activeWorkspaceId, 10) : 0;
  // cache-and-network so navigating back to billing reflects credits spent in the
  // meantime (e.g. AI agent runs) instead of showing the stale snapshot from last
  // visit. Cache-first (Apollo's default) returns stale totals until a refetch.
  const { data: creditData, loading: creditLoading } = useGetCreditUsageSummaryQuery({
    variables: { workspaceId: workspaceIdNum },
    skip: preview || !activeWorkspaceId || !workspaceIdNum,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const billingInfo: BillingInfo | null = useMemo(() => {
    if (preview) {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      const billingPeriod = `${formatDate(monthStart, language, fmt)} – ${formatDate(monthEnd, language, fmt)}`;

      const pack10kPurchased = new Date(now.getFullYear(), now.getMonth() - 8, 3);
      const pack10kExpires = new Date(pack10kPurchased);
      pack10kExpires.setFullYear(pack10kExpires.getFullYear() + 1);
      const pack25kPurchased = new Date(now.getFullYear(), now.getMonth() - 2, 14);
      const pack25kExpires = new Date(pack25kPurchased);
      pack25kExpires.setFullYear(pack25kExpires.getFullYear() + 1);

      const creditSources: CreditSource[] = [
        {
          __typename: 'CreditSource',
          id: 'subscription',
          kind: CreditSourceKind.Subscription,
          label: 'Subscription credits (5 seats × 2,000)',
          totalCredits: 10000,
          usedCredits: 2370,
          remainingCredits: 7630,
          expiresAt: monthEnd.toISOString(),
          purchasedAt: null,
        },
        {
          __typename: 'CreditSource',
          id: 'pack-10k',
          kind: CreditSourceKind.Pack,
          label: '10,000 credit pack',
          totalCredits: 10000,
          usedCredits: 10000,
          remainingCredits: 0,
          expiresAt: pack10kExpires.toISOString(),
          purchasedAt: pack10kPurchased.toISOString(),
        },
        {
          __typename: 'CreditSource',
          id: 'pack-25k',
          kind: CreditSourceKind.Pack,
          label: '25,000 credit pack',
          totalCredits: 25000,
          usedCredits: 6200,
          remainingCredits: 18800,
          expiresAt: pack25kExpires.toISOString(),
          purchasedAt: pack25kPurchased.toISOString(),
        },
      ];

      return {
        planName: 'Grande',
        planPrice: 50,
        billingCycle: 'month',
        billingPeriod,
        currency: 'USD',
        trialDaysRemaining: 12,
        isInTrial: true,
        subscriptionStatus: 'trialing',
        totalCreditsUsed: 18570,
        totalCreditsLimit: 45000,
        subscriptionCreditsLimit: 10000, // 5 seats × 2,000 (matches subscription source)
        creditSources,
        perAgent: null,
        seats: 5,
      };
    }

    // Subscription-derived fields (may be absent for free/no-subscription workspaces)
    const planPrice = subscription?.amountInCents ? subscription.amountInCents / 100 : 0;
    const planName = subscription?.pricingTier || 'Free';
    const billingCycle = subscription?.billingCycle || 'month';
    const currency = subscription?.currency || 'USD';
    const subscriptionStatus = subscription?.status || 'none';
    const seats = subscription?.seats || 0;

    // Format billing period from subscription dates (empty string if no subscription)
    let billingPeriod = '';
    if (subscription?.currentPeriodStart && subscription?.currentPeriodEnd) {
      const startDate = new Date(subscription.currentPeriodStart);
      const endDate = new Date(subscription.currentPeriodEnd);
      const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      billingPeriod = `${formatDate(startDate, language, formatOptions)} – ${formatDate(endDate, language, formatOptions)}`;
    }

    // Trial info (null if no subscription)
    const trialDaysRemaining = subscription ? calculateTrialDaysRemaining(subscription.freeTrialEnd) : null;
    const isInTrial = trialDaysRemaining !== null && trialDaysRemaining > 0;

    // Credit data from the creditUsageSummary query (works with or without subscription)
    const summary = creditData?.creditUsageSummary;
    const totalCreditsUsed = summary?.totalCreditsUsed ?? null;
    const totalCreditsLimit = summary?.totalCreditsLimit ?? null;
    const subscriptionCreditsLimit = summary?.subscriptionCreditsLimit ?? null;
    const perAgent = summary?.perAgent?.filter((a): a is AgentCreditUsage => a != null) ?? null;
    const creditSources = summary?.creditSources ?? null;

    // Use billing period from credit summary if subscription doesn't provide one
    if (!billingPeriod && summary?.billingPeriodStart && summary?.billingPeriodEnd) {
      const startDate = new Date(summary.billingPeriodStart);
      const endDate = new Date(summary.billingPeriodEnd);
      const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      billingPeriod = `${formatDate(startDate, language, formatOptions)} – ${formatDate(endDate, language, formatOptions)}`;
    }

    return {
      planName,
      planPrice,
      billingCycle,
      billingPeriod,
      currency,
      trialDaysRemaining,
      isInTrial,
      subscriptionStatus,
      totalCreditsUsed,
      totalCreditsLimit,
      subscriptionCreditsLimit,
      creditSources,
      perAgent,
      seats,
    };
  }, [preview, subscription, creditData, language]);

  return {
    billingInfo,
    subscription: subscription ?? null,
    loading: userLoading || creditLoading,
    workspaceId: activeWorkspaceId,
  };
}
